import styled, { css, keyframes } from 'styled-components';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useLayoutEffect } from 'react';

const BASE = `'Roboto', -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif`;
const CONDENSED = `'Roboto Condensed', ${BASE}`;

var font = /*#__PURE__*/Object.freeze({
  __proto__: null,
  BASE: BASE,
  CONDENSED: CONDENSED
});

const getSpacing = () => ({
  /**
   * Space API
   */
  x0: 0,
  x0_25: 2,
  x0_5: 4,
  x0_75: 6,
  x1: 8,
  x1_25: 10,
  x1_5: 12,
  x1_75: 14,
  x2: 16,
  x3: 24,
  x4: 32,
  x5: 40,
  x6: 48,
  x7: 56,
  x8: 64,
  x9: 72,
  x10: 80,
  x11: 88,
  x12: 96,
  x13: 104,
  x14: 112,
  x15: 120,
  x16: 128,
  x17: 136,
  x18: 144,
  x19: 152,
  x20: 160,
  /**
   * Deprecated Space API
   */
  x0space: 0,
  x0spacePx: 0,
  x1space: 0.8,
  x1spacePx: 8,
  x2space: 1.6,
  x2spacePx: 16,
  x3space: 2.4,
  x3spacePx: 24,
  x4space: 3.2,
  x4spacePx: 32,
  x5space: 4.0,
  x5spacePx: 40,
  x6space: 4.8,
  x6spacePx: 48,
  x7space: 5.6,
  x7spacePx: 56,
  x8space: 6.4,
  x8spacePx: 64,
  x9space: 7.2,
  x9spacePx: 72,
  x10space: 8.0,
  x10spacePx: 80,
  x11space: 8.8,
  x11spacePx: 88,
  x12space: 9.6,
  x12spacePx: 96,
  x13space: 10.4,
  x13spacePx: 104,
  x14space: 11.2,
  x14spacePx: 112,
  x15space: 12.0,
  x15spacePx: 120,
  x16space: 12.8,
  x16spacePx: 128,
  x17space: 13.6,
  x17spacePx: 136,
  x18space: 14.4,
  x18spacePx: 144,
  x19space: 15.2,
  x19spacePx: 152,
  x20space: 16.0,
  x20spacePx: 160
});

const screenSizeDefault = {
  sm: 576,
  md: 768,
  lg: 1024,
  xl: 1200
};
const screenSizeEnforceMobile = {
  sm: 3000,
  md: 4000,
  lg: 5000,
  xl: 6000
};
const screenSizeEnforcePrint = {
  sm: 300,
  md: 400,
  lg: 500,
  xl: 1500 //Enlarge A1 and A2 main container
};

var ScreenSize;
(function (ScreenSize) {
  ScreenSize[ScreenSize["Default"] = 0] = "Default";
  ScreenSize[ScreenSize["Mobile"] = 1] = "Mobile";
  ScreenSize[ScreenSize["Print"] = 2] = "Print";
})(ScreenSize || (ScreenSize = {}));
const getScreenSize = requiredScreenSize => {
  switch (requiredScreenSize) {
    case ScreenSize.Mobile:
      return screenSizeEnforceMobile;
    case ScreenSize.Print:
      return screenSizeEnforcePrint;
    default:
      return screenSizeDefault;
  }
};
const getBreakpoints = function () {
  let requiredScreenSize = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ScreenSize.Default;
  const screenSize = getScreenSize(requiredScreenSize);
  return {
    smMin: screenSize.sm + 'px',
    mdMin: screenSize.md + 'px',
    lgMin: screenSize.lg + 'px',
    xlMin: screenSize.xl + 'px',
    xsMax: screenSize.sm - 1 + 'px',
    smMax: screenSize.md - 1 + 'px',
    mdMax: screenSize.lg - 1 + 'px',
    lgMax: screenSize.xl - 1 + 'px',
    smMinRaw: screenSize.sm,
    mdMinRaw: screenSize.md,
    lgMinRaw: screenSize.lg,
    xlMinRaw: screenSize.xl,
    xsMaxRaw: screenSize.sm - 1,
    smMaxRaw: screenSize.md - 1,
    mdMaxRaw: screenSize.lg - 1,
    lgMaxRaw: screenSize.xl - 1
  };
};

const media$1 = getBreakpoints();
const spacing = getSpacing();
const getFontSizeScalar = unit => unit === 'px' ? 10 : 1;
const getFontSizeWithUnit = function (size) {
  let unit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'rem';
  return css`
    font-size: ${size * getFontSizeScalar(unit)}${unit};
  `;
};
const strong = css`
  font-weight: 500;
`;
const condensed = css`
  font-family: ${CONDENSED};
`;
const headingLineHeight = css`
  line-height: 1.25;
`;
const paragraphLineHeight = css`
  line-height: 1.5;
`;
const uber = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x5space, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x7space, unit)};
    }
    ${headingLineHeight}
  `;
const mass = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x4space + 0.4, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x6space, unit)};
    }
    ${headingLineHeight}
  `;
const hero = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x4space - 0.2, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x4space + 0.4, unit)};
    }
    ${headingLineHeight}
  `;
const mega = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x3space, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x3space + 0.4, unit)};
    }
    ${headingLineHeight}
  `;
const xLarge = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x3space - 0.3, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x3space, unit)};
    }
    ${headingLineHeight}
  `;
const large = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x2space + 0.2, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x2space + 0.4, unit)};
    }
    ${paragraphLineHeight}
  `;
const base = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x2space, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x2space + 0.2, unit)};
    }
    ${paragraphLineHeight}
  `;
const small = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x2space - 0.2, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x2space, unit)};
    }
    ${paragraphLineHeight}
  `;
const xSmall = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x1space + 0.5, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x2space - 0.2, unit)};
    }
    ${paragraphLineHeight}
  `;
const tiny = unit => css`
    font-weight: normal;
    ${getFontSizeWithUnit(spacing.x1space + 0.3, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x1space + 0.4, unit)};
    }
    ${paragraphLineHeight}
  `;
const inherit = css`
  font-family: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
`;
const uberStrong = unit => css`
    ${uber(unit)}
    ${strong}
  `;
const massStrong = unit => css`
    ${mass(unit)}
    ${strong}
  `;
const heroStrong = unit => css`
    ${hero(unit)}
    ${strong}
  `;
const megaStrong = unit => css`
    ${mega(unit)}
    ${strong}
  `;
const xLargeStrong = unit => css`
    ${xLarge(unit)}
    ${strong}
  `;
const largeStrong = unit => css`
    ${large(unit)}
    ${strong}
  `;
const baseStrong = unit => css`
    ${base(unit)}
    ${strong}
  `;
const smallStrong = unit => css`
    ${small(unit)}
    ${strong}
  `;
const xSmallStrong = unit => css`
    ${xSmall(unit)}
    ${strong}
  `;
const tinyStrong = unit => css`
    ${tiny(unit)}
    ${strong}
  `;
const uberCondensed = unit => css`
    ${uber(unit)}
    ${condensed}
  `;
const massCondensed = unit => css`
    ${mass(unit)}
    ${condensed}
  `;
const heroCondensed = unit => css`
    ${hero(unit)}
    ${condensed}
  `;
const megaCondensed = unit => css`
    ${mega(unit)}
    ${condensed}
  `;
const xLargeCondensed = unit => css`
    ${xLarge(unit)}
    ${condensed}
  `;
const largeCondensed = unit => css`
    ${large(unit)}
    ${condensed}
  `;
const baseCondensed = unit => css`
    ${base(unit)}
    ${condensed}
  `;
const smallCondensed = unit => css`
    ${small(unit)}
    ${condensed}
  `;
const xSmallCondensed = unit => css`
    ${xSmall(unit)}
    ${condensed}
  `;
const tinyCondensed = unit => css`
    ${tiny(unit)}
    ${condensed}
  `;
const editorHeading = unit => css`
    ${headingLineHeight}
    ${getFontSizeWithUnit(spacing.x3space, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x4space - 0.2, unit)};
      line-height: 1.333333; // 40px;
    }
    ${strong}
  `;
const editorSubHeading = unit => css`
    ${getFontSizeWithUnit(spacing.x2space + 0.4, unit)};
    line-height: 1.4;
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x3space, unit)};
      line-height: 1.333333;
    }
    ${strong}
  `;
const editorBody = unit => css`
    font-weight: 400;
    line-height: 1.75;
    ${getFontSizeWithUnit(spacing.x2space, unit)};
    @media (min-width: ${media$1.lgMin}) {
      ${getFontSizeWithUnit(spacing.x2space + 0.2, unit)};
      line-height: 1.555555; // 28px;
    }
  `;
const editorBodyStrong = unit => css`
    ${editorBody(unit)}
    font-weight: 700;
  `;
const textStyleBaseFontSize = css`
  font-family: ${BASE};
  font-weight: normal;
  ${getFontSizeWithUnit(spacing.x2space - 0.2)};
  @media (min-width: ${media$1.lgMin}) {
    ${getFontSizeWithUnit(spacing.x2space)};
  }
`;
const textStyleH1CSS = css`
  ${getFontSizeWithUnit(spacing.x4space - 0.4)};
  font-family: ${BASE};
  font-weight: 300;
  line-height: 1.5;
  @media (min-width: ${media$1.lgMin}) {
    ${getFontSizeWithUnit(spacing.x5space)};
  }
`;
const textStyleH2CSS = css`
  ${getFontSizeWithUnit(spacing.x3space)};
  font-family: ${BASE};
  font-weight: 700;
  line-height: 1.4;
  @media (min-width: ${media$1.lgMin}) {
    ${getFontSizeWithUnit(spacing.x4space - 0.2)};
  }
`;
const textStyleH3CSS = css`
  ${getFontSizeWithUnit(spacing.x3space - 0.4)};
  font-family: ${BASE};
  font-weight: 700;
  line-height: 1.5;
  @media (min-width: ${media$1.lgMin}) {
    font-size: ${spacing.x3space}rem;
  }
`;
const textStyleH4CSS = css`
  font-weight: normal;
  ${getFontSizeWithUnit(spacing.x2space - 0.2)};
  @media (min-width: ${media$1.lgMin}) {
    ${getFontSizeWithUnit(spacing.x2space)};
  }
`;
const textStylePCSS = css`
  ${textStyleBaseFontSize};
  line-height: 1.7;
`;
const getTypography = unit => ({
  uber: uber(unit),
  uberStrong: uberStrong(unit),
  uberCondensed: uberCondensed(unit),
  mass: mass(unit),
  massStrong: massStrong(unit),
  massCondensed: massCondensed(unit),
  hero: hero(unit),
  heroStrong: heroStrong(unit),
  heroCondensed: heroCondensed(unit),
  mega: mega(unit),
  megaStrong: megaStrong(unit),
  megaCondensed: megaCondensed(unit),
  xLarge: xLarge(unit),
  xLargeStrong: xLargeStrong(unit),
  xLargeCondensed: xLargeCondensed(unit),
  large: large(unit),
  largeStrong: largeStrong(unit),
  largeCondensed: largeCondensed(unit),
  base: base(unit),
  baseStrong: baseStrong(unit),
  baseCondensed: baseCondensed(unit),
  small: small(unit),
  smallStrong: smallStrong(unit),
  smallCondensed: smallCondensed(unit),
  xSmall: xSmall(unit),
  xSmallStrong: xSmallStrong(unit),
  xSmallCondensed: xSmallCondensed(unit),
  tiny: tiny(unit),
  tinyStrong: tinyStrong(unit),
  tinyCondensed: tinyCondensed(unit),
  editorHeading: editorHeading(unit),
  editorSubHeading: editorSubHeading(unit),
  editorBody: editorBody(unit),
  editorBodyStrong: editorBodyStrong(unit),
  inherit,
  strong,
  condensed,
  headingLineHeight,
  paragraphLineHeight
});

var typography = /*#__PURE__*/Object.freeze({
  __proto__: null,
  textStyleBaseFontSize: textStyleBaseFontSize,
  textStyleH1CSS: textStyleH1CSS,
  textStyleH2CSS: textStyleH2CSS,
  textStyleH3CSS: textStyleH3CSS,
  textStyleH4CSS: textStyleH4CSS,
  textStylePCSS: textStylePCSS,
  getTypography: getTypography
});

const reveal = keyframes`
	from 				{opacity: 0;}
	to 					{opacity: 1;}
`;
const Wrapper$1 = styled('div')`
  animation: ${reveal} 0.2s linear forwards;
  position: relative;
  ${_ref => {
  let {
    theme
  } = _ref;
  return theme.typography.tiny;
}};
  color: ${_ref2 => {
  let {
    type,
    theme
  } = _ref2;
  return theme.color[type === 'error' ? 'bad' : 'good'];
}};
  > span {
    display: inline-block;
    position: absolute;
    top: 2px;
    left: 0;
  }
`;

const ValidationMessage = _ref => {
  let {
    name,
    type,
    message
  } = _ref;
  return jsx(Wrapper$1, {
    type: type,
    children: jsx("span", {
      "data-cy-id": name,
      "data-testid": "validation-message",
      children: message
    })
  });
};

const placeHolder = css`
  ${_ref => {
  let {
    theme,
    styleType
  } = _ref;
  const color = styleType === 'compactReverse' || styleType === 'standardReverse' ? theme.color.spaceGrayRGBA(0.3) : theme.color.whiteRGBA(0.3);
  return `
      ::-webkit-input-placeholder {
        color: ${color};
      }
      :-moz-placeholder {
        color: ${color};
      }
      ::-moz-placeholder {
        color: ${color};
      }
      :-ms-input-placeholder {
        color: ${color};
      }
      ::-ms-input-placeholder {
        color: ${color};
      }
    `;
}}
`;
const labelColor = css`
  color: ${_ref2 => {
  let {
    theme,
    styleType
  } = _ref2;
  if (styleType === 'compactReverse' || styleType === 'standardReverse') {
    return theme.color.spaceGray;
  }
  return theme.color.white;
}};
`;
const getHeight = (theme, styleType) => {
  if (styleType === 'compactReverse' || styleType === 'compact') {
    return theme.x4spacePx;
  }
  return theme.x5spacePx;
};
const height = css`
  height: ${_ref3 => {
  let {
    theme,
    styleType
  } = _ref3;
  return getHeight(theme, styleType);
}}px;
`;
const getColor = (theme, styleType) => {
  if (styleType === 'compactReverse' || styleType === 'standardReverse') {
    return theme.color.spaceGray;
  }
  return theme.color.white;
};
const color = css`
  color: ${_ref4 => {
  let {
    theme,
    styleType
  } = _ref4;
  return getColor(theme, styleType);
}};
`;
const SVGfill = css`
  fill: ${_ref5 => {
  let {
    theme,
    styleType
  } = _ref5;
  return getColor(theme, styleType);
}};
`;
const background = css`
  background: ${_ref6 => {
  let {
    theme,
    styleType
  } = _ref6;
  if (styleType === 'compactReverse' || styleType === 'standardReverse') {
    return theme.color.spaceGrayRGBA(0.05);
  }
  return theme.color.whiteRGBA(0.05);
}};
`;
const border = css`
  border: ${_ref7 => {
  let {
    theme,
    styleType,
    error
  } = _ref7;
  if (error) {
    return `2px solid ${theme.color.bad}`;
  }
  if (styleType === 'compactReverse' || styleType === 'standardReverse') {
    return `1px solid ${theme.color.spaceGrayRGBA(0.2)}`;
  }
  return `1px solid ${theme.color.whiteRGBA(0.2)}`;
}};
`;
const fix = css`
  position: absolute;
  bottom: 0px;
  ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.typography.xSmall;
}}
  ${color}
  ${height}
  line-height: ${_ref9 => {
  let {
    theme,
    styleType
  } = _ref9;
  return getHeight(theme, styleType) + 1;
}}px;
  > svg {
    height: 100%;
    * {
      ${SVGfill}
    }
  }
`;
const Wrapper = styled('fieldset')`
  margin: 0;
  position: relative;
  padding-top: ${_ref10 => {
  let {
    label,
    theme
  } = _ref10;
  return label ? theme.x3spacePx : 0;
}}px;
  > label {
    display: block;
    opacity: 0.7;
    ${labelColor}
  }

  ${_ref11 => {
  let {
    pre,
    prefixPadding,
    theme
  } = _ref11;
  return pre && css`
      > input {
        padding-left: ${theme.x3spacePx + (prefixPadding || theme.x3spacePx)}px;
      }
    `;
}};
  ${_ref12 => {
  let {
    post,
    postfixPadding,
    theme
  } = _ref12;
  return post && css`
      > input {
        padding-right: ${theme.x3spacePx + (postfixPadding || theme.x3spacePx)}px;
      }
    `;
}};
`;
const Label = styled('label')`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  margin-bottom: 0;
  transition: opacity 0.3s;
  ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.typography.xSmall;
}}
`;
const Prefix = styled('span')`
  ${fix}
  left: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.x2spacePx - 4;
}}px;
`;
const Postfix = styled('span')`
  ${fix}
  right: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.x2spacePx - 4;
}}px;
`;
const Input = styled('input')`
  width: 100%;
  padding-left: ${_ref16 => {
  let {
    theme
  } = _ref16;
  return theme.x2spacePx - 4;
}}px;
  padding-right: ${_ref17 => {
  let {
    theme
  } = _ref17;
  return theme.x2spacePx - 4;
}}px;
  border-radius: 4px;

  ${_ref18 => {
  let {
    theme
  } = _ref18;
  return theme.typography.xSmall;
}}

  ${height}
  line-height: normal;
  ${color}
  ${border}
  ${background}
	transition: border 0.2s, background 0.2s;

  caret-color: ${_ref19 => {
  let {
    theme
  } = _ref19;
  return theme.color.brand01;
}};

  &:focus {
    background: ${_ref20 => {
  let {
    theme
  } = _ref20;
  return theme.color.brand01RGBA(0.05);
}};
    & + span + label {
      opacity: 1;
    }
    & + span {
      position: absolute;
      width: 100%;
      left: 0;
      bottom: ${_ref21 => {
  let {
    theme,
    styleType
  } = _ref21;
  return getHeight(theme, styleType);
}}px;
      &:after {
        height: ${_ref22 => {
  let {
    theme,
    styleType
  } = _ref22;
  return getHeight(theme, styleType) + 6;
}}px;
        border-radius: 8px;
      }
    }
  }

  &:disabled {
    opacity: 0.3;
    & + label {
      opacity: 0.3;
    }
  }

  &[type='search'] {
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    ${_ref23 => {
  let {
    theme
  } = _ref23;
  return theme.typography.xSmall;
}}
    -webkit-text-fill-color: ${_ref24 => {
  let {
    theme,
    styleType
  } = _ref24;
  return getColor(theme, styleType);
}};
  }

  ${placeHolder}
`;
const Symbol = styled('span')`
  display: inline-block;
  opacity: 0.5;
`;

const Textfield = forwardRef(function InputComponent(_ref, ref) {
  let {
    styleType,
    autoComplete,
    autoFocus,
    disabled,
    error,
    label,
    maxLength,
    minLength,
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    onKeyDown,
    onKeyPress,
    placeholder,
    prefix,
    postfix,
    readOnly,
    success,
    tabIndex,
    type,
    value,
    defaultValue,
    inputMode,
    pattern
  } = _ref;
  const prefixRef = useRef(null);
  const postfixRef = useRef(null);
  const [prefixPadding, setPrefixPadding] = useState(24);
  const [postfixPadding, setPostfixPadding] = useState(24);
  useLayoutEffect(() => {
    const {
      current: pre
    } = prefixRef;
    const {
      current: post
    } = postfixRef;
    let width = 0;
    if (pre) {
      width = Math.ceil(pre.getBoundingClientRect().width);
      if (width !== prefixPadding) {
        setPrefixPadding(width);
      }
    }
    if (post) {
      width = Math.ceil(post.getBoundingClientRect().width);
      if (width !== postfixPadding) {
        setPostfixPadding(width);
      }
    }
  }, [prefixPadding, postfixPadding]);
  return jsxs(Wrapper, {
    styleType: styleType,
    label: label,
    pre: prefix !== undefined && prefix !== null,
    post: postfix !== undefined && postfix !== null,
    prefixPadding: prefixPadding,
    postfixPadding: postfixPadding,
    children: [jsx(Input, {
      autoComplete: autoComplete,
      type: type,
      name: name,
      styleType: styleType,
      maxLength: maxLength,
      minLength: minLength,
      autoFocus: autoFocus,
      error: error,
      tabIndex: tabIndex,
      ref: ref,
      placeholder: placeholder,
      onChange: onChange,
      onClick: onClick,
      onBlur: onBlur,
      onFocus: onFocus,
      value: value,
      defaultValue: defaultValue,
      disabled: disabled,
      readOnly: readOnly,
      onKeyPress: onKeyPress,
      onKeyDown: onKeyDown,
      "data-cy-id": name,
      inputMode: inputMode,
      pattern: pattern
    }), jsx("span", {}), label && jsx(Label, {
      htmlFor: name,
      children: label
    }), success && jsx(ValidationMessage, {
      type: "success",
      name: `${name}-invalid-message`,
      message: success
    }), error && jsx(ValidationMessage, {
      type: "error",
      name: `${name}-invalid-message`,
      message: error
    }), prefix && jsx(Prefix, {
      "data-testid": "prefix",
      styleType: styleType,
      ref: prefixRef,
      children: prefix
    }), postfix && jsx(Postfix, {
      "data-testid": "postfix",
      styleType: styleType,
      ref: postfixRef,
      children: postfix
    })]
  });
});

const cubicBezierFunction = css`0.2s cubic-bezier(0.23, 1, 0.32, 1)`;
const ToggleLabel = styled('label')`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  padding: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.x1spacePx / 2;
}}px;

  /* Overwrite default theme */
  &&& {
    margin: 0px;
  }
`;
const ToggleLabelText = styled('span')`
  ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.typography.xSmall;
}}
  line-height: normal;

  margin-left: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.x1spacePx;
}}px;
`;
const ToggleSwitch = styled('span')`
  position: absolute;
  top: -${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.x1spacePx / 2;
}}px;
  left: -2px;
  transition: transform ${cubicBezierFunction};
  width: 20px;
  height: 20px;
  display: block;
  background: white;
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
`;
const ToggleBase = styled('span')`
  display: block;
  position: relative;
  transition: background ${cubicBezierFunction};
  background: ${_ref5 => {
  let {
    theme,
    styleType
  } = _ref5;
  return styleType === 'light' ? theme.color.lightGray : theme.color.darkerGray;
}};
  width: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.x5spacePx;
}}px;
  height: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.x1spacePx + 4;
}}px;
  border-radius: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.x3spacePx + 4;
}}px;
`;
const ToggleWrapper = styled('fieldset')`
  z-index: 0;
  user-select: none;
  display: ${_ref9 => {
  let {
    display
  } = _ref9;
  return display;
}};
  opacity: ${_ref10 => {
  let {
    disabled
  } = _ref10;
  return disabled ? 0.5 : 1;
}};
  transition: opacity ${cubicBezierFunction};
  color: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.color.white;
}};
  ${ToggleLabel} {
    cursor: ${_ref12 => {
  let {
    disabled
  } = _ref12;
  return disabled ? 'not-allowed' : 'pointer';
}};
    min-height: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.x3spacePx;
}}px;
  }
  input {
    opacity: 0;
    position: absolute;
    top: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.x1spacePx / 2;
}}px;
    left: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.x1spacePx / 2;
}}px;
    &:checked {
      + ${ToggleBase} {
        background: ${_ref16 => {
  let {
    theme
  } = _ref16;
  return theme.color.brand01;
}};
        > ${ToggleSwitch} {
          transform: translateX(${_ref17 => {
  let {
    theme
  } = _ref17;
  return theme.x3spacePx;
}}px);
          box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.6);
        }
      }
    }
  }
`;

const dashedFocus = css`
  ${_ref => {
  let {
    theme
  } = _ref;
  return `
    &:after {
      display: block;
      content: '';
      position: absolute;
      top: -3px;
      right: -3px;
      bottom: -3px;
      left: -3px;
      border: 1px dashed ${theme.color.brand01};
      border-radius: 8px;
    }
  `;
}}
`;
const solidFocus = css`
  ${_ref2 => {
  let {
    theme
  } = _ref2;
  return `
    background-color: ${theme.color.brand01};
    color: ${theme.color.white};
    box-shadow: none;
  `;
}}
`;
const attr = (theme, type) => Object.entries(theme.focus(type)).map(n => `${n[0]}="${n[1]}"`).join();
var focus = css`
  body:not(.using-mouse) {
    button[${_ref3 => {
  let {
    theme
  } = _ref3;
  return attr(theme, 'dashed');
}}]:focus {
      ${dashedFocus}
    }

    ${Input}:focus {
      + span {
        ${dashedFocus}
      }
    }

    ${ToggleLabel}:focus-within {
      ${dashedFocus}
    }
  }

  body:not(.using-mouse) [${_ref4 => {
  let {
    theme
  } = _ref4;
  return attr(theme, 'solid');
}}]:focus {
    ${solidFocus}
  }
`;

function darken(rgb, percent) {
  const arr = rgb.split(',');
  const r = +arr[0];
  const g = +arr[1];
  const b = +arr[2];
  return [Math.round(r - r * percent), Math.round(g - g * percent), Math.round(b - b * percent)].join();
}

const breakControl = function (condition, wA) {
  let wB = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '0';
  switch (condition) {
    case 'up':
      return '(min-width: ' + wA + ')';
    case 'down':
      return '(max-width: ' + wA + ')';
    case 'only':
      return '(min-width: ' + wA + ') and (max-width: ' + wB + ')';
    default:
      return '';
  }
};
const media = {
  xsDown: function (literals) {
    for (var _len = arguments.length, styles = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      styles[_key - 1] = arguments[_key];
    }
    return css`
    @media ${_ref => {
      let {
        theme
      } = _ref;
      return breakControl('down', theme.xsMax);
    }} {
      ${css(literals, ...styles)};
    }
  `;
  },
  smDown: function (literals) {
    for (var _len2 = arguments.length, styles = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      styles[_key2 - 1] = arguments[_key2];
    }
    return css`
      @media ${_ref2 => {
      let {
        theme
      } = _ref2;
      return breakControl('down', theme.smMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  mdDown: function (literals) {
    for (var _len3 = arguments.length, styles = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      styles[_key3 - 1] = arguments[_key3];
    }
    return css`
      @media ${_ref3 => {
      let {
        theme
      } = _ref3;
      return breakControl('down', theme.mdMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  lgDown: function (literals) {
    for (var _len4 = arguments.length, styles = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
      styles[_key4 - 1] = arguments[_key4];
    }
    return css`
      @media ${_ref4 => {
      let {
        theme
      } = _ref4;
      return breakControl('down', theme.lgMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  smUp: function (literals) {
    for (var _len5 = arguments.length, styles = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
      styles[_key5 - 1] = arguments[_key5];
    }
    return css`
      @media ${_ref5 => {
      let {
        theme
      } = _ref5;
      return breakControl('up', theme.smMin);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  mdUp: function (literals) {
    for (var _len6 = arguments.length, styles = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
      styles[_key6 - 1] = arguments[_key6];
    }
    return css`
      @media ${_ref6 => {
      let {
        theme
      } = _ref6;
      return breakControl('up', theme.mdMin);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  lgUp: function (literals) {
    for (var _len7 = arguments.length, styles = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
      styles[_key7 - 1] = arguments[_key7];
    }
    return css`
      @media ${_ref7 => {
      let {
        theme
      } = _ref7;
      return breakControl('up', theme.lgMin);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  xlUp: function (literals) {
    for (var _len8 = arguments.length, styles = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
      styles[_key8 - 1] = arguments[_key8];
    }
    return css`
      @media ${_ref8 => {
      let {
        theme
      } = _ref8;
      return breakControl('up', theme.xlMin);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  xsOnly: function (literals) {
    for (var _len9 = arguments.length, styles = new Array(_len9 > 1 ? _len9 - 1 : 0), _key9 = 1; _key9 < _len9; _key9++) {
      styles[_key9 - 1] = arguments[_key9];
    }
    return css`
      @media ${_ref9 => {
      let {
        theme
      } = _ref9;
      return breakControl('down', theme.xsMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  smOnly: function (literals) {
    for (var _len10 = arguments.length, styles = new Array(_len10 > 1 ? _len10 - 1 : 0), _key10 = 1; _key10 < _len10; _key10++) {
      styles[_key10 - 1] = arguments[_key10];
    }
    return css`
      @media ${_ref10 => {
      let {
        theme
      } = _ref10;
      return breakControl('only', theme.smMin, theme.smMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  mdOnly: function (literals) {
    for (var _len11 = arguments.length, styles = new Array(_len11 > 1 ? _len11 - 1 : 0), _key11 = 1; _key11 < _len11; _key11++) {
      styles[_key11 - 1] = arguments[_key11];
    }
    return css`
      @media ${_ref11 => {
      let {
        theme
      } = _ref11;
      return breakControl('only', theme.mdMin, theme.mdMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  lgOnly: function (literals) {
    for (var _len12 = arguments.length, styles = new Array(_len12 > 1 ? _len12 - 1 : 0), _key12 = 1; _key12 < _len12; _key12++) {
      styles[_key12 - 1] = arguments[_key12];
    }
    return css`
      @media ${_ref12 => {
      let {
        theme
      } = _ref12;
      return breakControl('only', theme.lgMin, theme.lgMax);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  },
  xlOnly: function (literals) {
    for (var _len13 = arguments.length, styles = new Array(_len13 > 1 ? _len13 - 1 : 0), _key13 = 1; _key13 < _len13; _key13++) {
      styles[_key13 - 1] = arguments[_key13];
    }
    return css`
      @media ${_ref13 => {
      let {
        theme
      } = _ref13;
      return breakControl('up', theme.xlMin);
    }} {
        ${css(literals, ...styles)};
      }
    `;
  }
};

const hideScrollbar = css`
  /* Hide scrollbar */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

export { BASE as B, Input as I, ScreenSize as S, ToggleWrapper as T, ValidationMessage as V, ToggleLabel as a, ToggleBase as b, ToggleSwitch as c, ToggleLabelText as d, focus as e, font as f, darken as g, hideScrollbar as h, getBreakpoints as i, Textfield as j, Symbol as k, getTypography as l, media as m, getSpacing as n, typography as t };
